/**
 * Coral Product Gallery — astroexoticreef-theme/assets/coral-product.js
 *
 * Two affordances on the product page's custom image gallery
 * (sections/coral-product-details.liquid — a fully custom gallery, NOT
 * Dawn's stock product-media-gallery/product-thumbnail/product-media-modal
 * snippets, which exist elsewhere in this theme but aren't used here):
 *
 *   1. Clicking a thumbnail swaps the main image. Previously inert — the
 *      only JS that existed toggled the thumbnail's active-state class and
 *      never touched the main <img> at all.
 *   2. Clicking the main image (or its small zoom-icon overlay) opens a
 *      full-size zoom modal for close WYSIWYG inspection, with prev/next to
 *      browse every image on the product without closing the modal. The
 *      main gallery and the modal stay in sync in both directions.
 *
 * No dependencies. Loaded with `defer` from coral-product-details.liquid, so
 * the DOM is already parsed by the time this file runs. Re-initializes on
 * `shopify:section:load` so it keeps working inside the theme editor.
 */
(function () {
  'use strict';

  function initCoralGallery(root) {
    if (!root || root.dataset.coralGalleryInit === 'true') return;

    var mainImageWrap = root.querySelector('.coral-product__main-image');
    var mainImg        = mainImageWrap ? mainImageWrap.querySelector('.coral-product__featured-img') : null;
    var thumbButtons   = root.querySelectorAll('.coral-product__thumbnail');
    var zoomTriggers   = root.querySelectorAll('[data-zoom-open]');
    var dataScript     = root.querySelector('#coral-product-media-data');
    var modal          = document.getElementById('coral-zoom-modal');

    var mediaItems = [];
    if (dataScript) {
      try {
        mediaItems = JSON.parse(dataScript.textContent) || [];
      } catch (e) {
        mediaItems = [];
      }
    }

    // Nothing to wire up (e.g. a product with only the placeholder image).
    if (!mainImg && mediaItems.length === 0) return;

    root.dataset.coralGalleryInit = 'true';

    var currentIndex = 0;
    if (mainImageWrap && mainImageWrap.dataset.currentMediaId) {
      var initialIndex = indexForMediaId(mainImageWrap.dataset.currentMediaId);
      if (initialIndex > -1) currentIndex = initialIndex;
    }

    function indexForMediaId(mediaId) {
      for (var i = 0; i < mediaItems.length; i++) {
        if (String(mediaItems[i].id) === String(mediaId)) return i;
      }
      return -1;
    }

    function setActiveThumbnail(mediaId) {
      thumbButtons.forEach(function (btn) {
        var active = String(btn.dataset.mediaId) === String(mediaId);
        btn.classList.toggle('coral-product__thumbnail--active', active);
        btn.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    // Swaps the visible main image (the "base" gallery affordance).
    function swapMainImage(index) {
      var item = mediaItems[index];
      if (!item || !mainImg) return;
      currentIndex = index;
      mainImg.src = item.main;
      if (item.alt) mainImg.alt = item.alt;
      if (mainImageWrap) mainImageWrap.dataset.currentMediaId = item.id;
      setActiveThumbnail(item.id);
    }

    thumbButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var index = indexForMediaId(btn.dataset.mediaId);
        if (index === -1) return;
        swapMainImage(index);
        if (modal && modal.classList.contains('coral-zoom-modal--open')) {
          showZoomImage(index);
        }
      });
    });

    // ---- Zoom modal ----
    if (!modal || mediaItems.length === 0) return;

    var zoomImg     = modal.querySelector('.coral-zoom-modal__img');
    var counterEl   = modal.querySelector('.coral-zoom-modal__counter');
    var closeEls    = modal.querySelectorAll('[data-zoom-close]');
    var prevBtn     = modal.querySelector('[data-zoom-prev]');
    var nextBtn     = modal.querySelector('[data-zoom-next]');
    var lastFocused = null;
    var closeTimer  = null;

    // Updates the modal's image/counter only — does NOT touch the main
    // gallery (callers decide whether to also call swapMainImage()).
    function showZoomImage(index) {
      if (mediaItems.length === 0) return;
      index = ((index % mediaItems.length) + mediaItems.length) % mediaItems.length;
      var item = mediaItems[index];
      if (zoomImg) {
        zoomImg.src = item.zoom;
        zoomImg.alt = item.alt || '';
      }
      if (counterEl) counterEl.textContent = (index + 1) + ' / ' + mediaItems.length;
    }

    // Prev/next inside the modal should also update the main gallery behind
    // it, so closing the modal leaves you on whatever image you last viewed.
    function goToZoomImage(index) {
      index = ((index % mediaItems.length) + mediaItems.length) % mediaItems.length;
      showZoomImage(index);
      swapMainImage(index);
    }

    function focusableModalEls() {
      return Array.prototype.filter.call(modal.querySelectorAll('button'), function (el) {
        return el.offsetParent !== null;
      });
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closeModal();
        return;
      }
      if (mediaItems.length > 1) {
        if (e.key === 'ArrowRight') {
          goToZoomImage(currentIndex + 1);
          return;
        }
        if (e.key === 'ArrowLeft') {
          goToZoomImage(currentIndex - 1);
          return;
        }
      }
      if (e.key === 'Tab') {
        var items = focusableModalEls();
        if (items.length === 0) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function openModal(index, triggerEl) {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      lastFocused = triggerEl || document.activeElement;
      showZoomImage(typeof index === 'number' && index > -1 ? index : currentIndex);
      modal.hidden = false;
      // Double rAF so the [hidden] -> visible flip is committed to the layout
      // before the transition class is added (otherwise some browsers skip
      // the transition entirely on the very first open).
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('coral-zoom-modal--open');
        });
      });
      document.body.classList.add('coral-zoom-modal-lock');
      document.addEventListener('keydown', onKeydown);
      var closeBtn = modal.querySelector('.coral-zoom-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    function closeModal() {
      modal.classList.remove('coral-zoom-modal--open');
      document.body.classList.remove('coral-zoom-modal-lock');
      document.removeEventListener('keydown', onKeydown);
      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var finish = function () {
        modal.hidden = true;
      };
      if (prefersReduced) {
        finish();
      } else {
        closeTimer = setTimeout(finish, 220);
      }
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    zoomTriggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(currentIndex, trigger);
      });
    });

    closeEls.forEach(function (el) {
      el.addEventListener('click', closeModal);
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { goToZoomImage(currentIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToZoomImage(currentIndex + 1); });
  }

  function init() {
    document.querySelectorAll('.coral-product').forEach(initCoralGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run when this section (or a section containing it) is
  // reloaded, e.g. after a merchant edits a setting.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('.coral-product') : null;
    if (root) initCoralGallery(root);
  });
})();
